import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { changeLanguage, getCurrentLanguage } from '../i18n';

interface LanguageContextType {
  currentLanguage: string;
  setLanguage: (language: string) => Promise<void>;
  isChangingLanguage: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [currentLanguage, setCurrentLanguage] =
    useState<string>(getCurrentLanguage());
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);

  const setLanguage = async (language: string) => {
    setIsChangingLanguage(true);
    try {
      await changeLanguage(language);
      setCurrentLanguage(language);
      // Store in localStorage for persistence
      localStorage.setItem('selectedLanguage', language);
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsChangingLanguage(false);
    }
  };

  // Load language preference on mount — once only: re-running on language
  // changes would re-apply the saved value over the user's new choice.
  useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage && savedLanguage !== currentLanguage) {
      setLanguage(savedLanguage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: LanguageContextType = {
    currentLanguage,
    setLanguage,
    isChangingLanguage,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
